import { useState } from 'react';

import { ORDER_TYPES } from '@wareclouds/frontend-common/b2c';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import RouteIcon from '@mui/icons-material/Route';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import SyncIcon from '@mui/icons-material/Sync';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import orderIcon from '../../assets/img/order.svg';
import { Button, Card, Col, Collapse, Row, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { ORDER_STATUSES } from '@wareclouds/frontend-common/b2c';
import dateParser from '../../utils/dateParser';
import DirectionsIcon from '@mui/icons-material/Directions';
import { Chip } from '@mui/material';
import { useDispatch } from 'react-redux';
import { openToDeliverToClientModals, setOrderRoute } from './orderSlice';
import { ExpressOrderTimeLimitLabel } from './components';
import { BarcodeScannerModal } from '../common/components/barcodeScanner';
import { useWLoading } from '../common';
import { EcommerceLogo } from '../common/components';
import { getOrderLimitTime } from './utils';
import { ORDER_TYPE_PICKUP_AT_WARECLOUD } from '../../config/consts/orders';

const CardColumn = ({ label, children, isDark }) => (
  <Col xs={12} md={6} className="p-0 my-2 pr-2">
    <p className={`order-card__label ${isDark && 'order-card__label--dark'}`}>
      {label}
    </p>
    {children}
  </Col>
);

const TimeLimitLabel = ({ label }) => {
  return <span>{`Solicitud cliente: ${label}`}</span>;
};

const OrderCard = ({
  buttonText,
  cardUser,
  handlePrompt,
  index,
  innerAttributes,
  isPending,
  order,
  orderedByOptimizedRoute,
  outerAttributes,
  toggleShowMoreInfo,
  view,
  validateCodeAndConfirmOrder,
  ...rest
}) => {
  const { t } = useTranslation(['system', 'glossary', 'description', 'button']);
  const {
    order_code,
    is_cold,
    ecommerce_logo,
    updated_at,
    ecommerce_name,
    ref,
    showMore,
    related_order,
    id_status_order,
    latitude,
    longitude,
    id_order_type: idOrderType,
    route,
    id_route,
  } = order;

  const isExpressOrder = [
    ORDER_TYPES.EXPRESS_SCHEDULED,
    ORDER_TYPES.EXPRESS,
  ].includes(idOrderType);

  const isExpressScheduled = idOrderType === ORDER_TYPES.EXPRESS_SCHEDULED;

  const isExpressPickUpInStore = idOrderType === ORDER_TYPES.EXPRESS;
  const shouldShowPickupBadge =
    view === 'toDeliver' && idOrderType === ORDER_TYPE_PICKUP_AT_WARECLOUD;
  const dispatch = useDispatch();
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  const { handleOpen } = useWLoading();
  const isUnavailable = rest.unavailable;

  const hasLimitTime =
    !!order?.order_limit_delivery_time?.id_order_limit_delivery_time;
  const time_from = order?.order_limit_delivery_time?.time_from;
  const time_until = order?.order_limit_delivery_time?.time_until;

  const expressOrderLimitTime = isExpressScheduled
    ? getOrderLimitTime({
        orderFrom: time_from,
        orderUntil: time_until,
        routeCreateAt: route?.created_at,
      })
    : time_until;

  const hasLatitudeAndLongitude = !!latitude && !!longitude;
  const buttonTextByOrderStatus = () => {
    if (id_status_order === ORDER_STATUSES.TO_ASSEMBLE)
      return t('ORDER_WITHDRAWAL_UNAVAILABLE_TO_ASSEMBLE', { ns: 'system' });

    return t('ORDER_WITHDRAWAL_UNAVAILABLE_ASSEMBLED', { ns: 'system' });
  };

  const getPendingButtonText = () => {
    if (rest.pendingButtonText) return rest.pendingButtonText;

    if (id_status_order === ORDER_STATUSES.READY_TO_BE_DELIVERED)
      return t('ORDER_WITHDRAWAL_READY_TO_BE_DELIVERED', {
        ns: 'system',
      });

    return t('ORDER_WITHDRAWAL_UNAVAILABLE_CONFIRMATION', {
      ns: 'system',
    });
  };

  const renderColumns = ({ payload, columnType }) => {
    return payload.map((attribute, index) => {
      const isPhoneNumber =
        attribute === 'warecloud_phone' || attribute === 'consumer_phone';
      const value = order[attribute];

      let content = (
        <Col
          xs={12}
          md={6}
          className="p-0 my-2 pr-2"
          key={`${index}-attribute-col-${columnType}-${order_code}`}
        >
          <Button
            className="order-card__whatsapp-button"
            href={`https://wa.me/+56${value}`}
          >
            {t('contact', { ns: 'glossary' })} <WhatsAppIcon />
          </Button>
        </Col>
      );

      if (
        (isPhoneNumber && view === 'toRetrieve') ||
        (isPhoneNumber && view === 'toReturn')
      ) {
        return content;
      } else if (isPhoneNumber) {
        content = (
          <a
            href={`tel:${value}`}
            className="order-card__text order-card__text--phone"
          >
            {value}
          </a>
        );
      } else {
        content = <p className="order-card__text">{value ? value : '-'}</p>;
      }

      return (
        <CardColumn
          key={`${index}-attribute-col-${columnType}-${order_code}`}
          label={t(attribute, { ns: 'glossary' })}
          isDark={isUnavailable}
        >
          {content}
        </CardColumn>
      );
    });
  };
  const handleMapRoute = () => {
    dispatch(setOrderRoute({ latitude, longitude }));
    dispatch(openToDeliverToClientModals('mapRoute'));
  };

  const onCodeScanned = (code) => {
    handleOpen();
    validateCodeAndConfirmOrder(code, order);
    setShowBarcodeScanner(false);
  };

  const onDeliverOrder = () => {
    const isNaturaOrder = (order?.ecommerce_name || '')
      ?.replace(/\s+/g, '')
      ?.toLowerCase()
      .trim()
      .includes('naturacd');

    handlePrompt({ order, isNaturaOrder });
  };

  return (
    <>
      <Card
        className={`order-card ${isUnavailable && 'order-card--grey'}`}
        ref={ref}
      >
        <Card.Body className="py-3 px-4">
          {shouldShowPickupBadge && (
            <div className="order-card__pickup-badge">Retiro</div>
          )}
          <Row>
            <Col xs={isExpressOrder ? 7 : 12} md="6" className="px-0 no-select">
              <div>
                <div className="d-flex align-items-center">
                  <img
                    src={orderIcon}
                    alt="order"
                    className="me-2"
                    style={{ width: '20px', height: '20px' }}
                  />
                  <p className="order-card__title mb-0">
                    {order_code}{' '}
                    {is_cold && <AcUnitIcon className="cold-icon" />}
                    {related_order && <SyncIcon className="replacement-icon" />}
                  </p>
                </div>
                {(id_route || route?.id_route) && (
                  <div className="d-flex align-items-center mt-1">
                    <Chip
                      icon={<RouteIcon style={{ fontSize: '14px' }} />}
                      label={id_route || route?.id_route}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontSize: '12px',
                        height: '24px',
                        fontWeight: 600,
                        '& .MuiChip-label': {
                          color: 'text.secondary',
                          fontWeight: 600,
                        },
                      }}
                    />
                  </div>
                )}
              </div>
            </Col>
            {isExpressOrder && (
              <Col xs="5" md="6" className="px-0">
                <ExpressOrderTimeLimitLabel timeUntil={expressOrderLimitTime} />
              </Col>
            )}

            {hasLimitTime && !isExpressOrder && (
              <Col xs="12" className="px-0">
                {(time_from || time_until) && (
                  <div className="order-card__limit-time">
                    <AccessTimeIcon />
                    <TimeLimitLabel
                      label={t(
                        time_from
                          ? 'orders.deliver.time_from_label'
                          : 'orders.deliver.time_until_label',
                        {
                          ns: 'description',
                          time: time_from || time_until,
                        }
                      )}
                    />
                  </div>
                )}
              </Col>
            )}
          </Row>
          <Row>
            <Col className="px-0 mt-2">
              {related_order && (
                <strong>{t('orders.related', { ns: 'description' })}</strong>
              )}
            </Col>
          </Row>
          <Row className="my-4">
            <Col xs="auto" className="order-card__logo">
              <EcommerceLogo
                src={ecommerce_logo}
                className="img-fluid h-100 rounded"
              />
            </Col>
            <Col className="my-auto">
              <p
                className={`order-card__label order-card__text--large ${
                  isUnavailable && 'order-card__label--dark'
                }`}
              >
                {cardUser.label}
              </p>
              <p className="order-card__text order-card__text--large">
                {cardUser.value}
              </p>
            </Col>
          </Row>
          <Row>
            {renderColumns({ payload: outerAttributes, columnType: 'outer' })}
          </Row>

          <Collapse in={showMore}>
            <Row>
              {renderColumns({ payload: innerAttributes, columnType: 'inner' })}

              <CardColumn
                label={t('ecommerce_name', { ns: 'glossary' })}
                isDark={isUnavailable}
              >
                <p className="order-card__text">{ecommerce_name}</p>
              </CardColumn>

              <CardColumn
                label={t('updated_at', { ns: 'glossary' })}
                isDark={isUnavailable}
              >
                <p className="order-card__text">{dateParser(updated_at)}</p>
              </CardColumn>
              {rest.extra}
            </Row>
          </Collapse>

          <Row className="justify-content-between mt-3">
            <Col xs={12} md="auto" className="p-0 my-2">
              <Button
                className="order-card__return-button"
                onClick={() => {
                  toggleShowMoreInfo(index);
                }}
              >
                {showMore
                  ? t('orders.less_info', { ns: 'description' })
                  : t('orders.more_info', { ns: 'description' })}
                {showMore ? (
                  <KeyboardArrowUpIcon className="ms-2" />
                ) : (
                  <KeyboardArrowDownIcon className="ms-2" />
                )}
              </Button>
            </Col>
            <Col
              xs={hasLatitudeAndLongitude ? 8 : 12}
              md="auto"
              className="p-0 my-2"
            >
              {isExpressPickUpInStore && view === 'toRetrieve' ? (
                <Button
                  variant="primary"
                  className="w-100"
                  onClick={() => setShowBarcodeScanner(true)}
                >
                  {t('orders.code_scanner', { ns: 'description' })}
                </Button>
              ) : (
                <>
                  {isPending ? (
                    <Button
                      variant={isUnavailable ? 'secondary' : 'warning'}
                      className="w-100"
                      disabled
                    >
                      {rest.isSubmitting ? (
                        <>
                          <Spinner
                            animation="border"
                            size="sm"
                            className="me-2"
                          />
                          {t('saving', {
                            ns: 'button',
                            defaultValue: 'Cargando...',
                          })}
                        </>
                      ) : (
                        <>
                          {isUnavailable
                            ? buttonTextByOrderStatus()
                            : getPendingButtonText()}
                          <AccessTimeIcon
                            className={`text-${
                              isUnavailable ? 'white' : 'secondary'
                            }`}
                          />
                        </>
                      )}
                    </Button>
                  ) : (
                    <Button
                      className="w-100"
                      variant="primary"
                      onClick={onDeliverOrder}
                    >
                      {buttonText}
                    </Button>
                  )}
                </>
              )}
            </Col>
            {hasLatitudeAndLongitude && (
              <Col xs={3} md="auto" className="p-0 my-2">
                <Button
                  className="w-100"
                  variant="primary"
                  disabled={!latitude || !longitude}
                  onClick={handleMapRoute}
                >
                  <DirectionsIcon sx={{ fontSize: '18.5px' }} />
                </Button>
              </Col>
            )}
          </Row>
        </Card.Body>
      </Card>

      {showBarcodeScanner && (
        <BarcodeScannerModal
          open={showBarcodeScanner}
          onClose={() => setShowBarcodeScanner(false)}
          onCodeScanned={onCodeScanned}
        />
      )}
    </>
  );
};

export default OrderCard;
